import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Autorenew as RotateIcon,
  ContentCopy as CopyIcon,
  ToggleOn as EnableIcon,
  ToggleOff as DisableIcon,
} from '@mui/icons-material';
import Header from '../components/Header';
import {
  listProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  rotateProductSecret,
  Product,
  ProductSecretResponse,
} from '../services/api';

const ProductManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    product_id: '',
    name: '',
    default_callback_url: '',
  });
  // The one-time secret reveal (create / rotate). Held only in local state and
  // never re-fetchable — the server does not return it again.
  const [revealed, setRevealed] = useState<ProductSecretResponse | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listProducts();
      setProducts(data.products || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({ product_id: '', name: '', default_callback_url: '' });
    setError(null);
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    setError(null);
    if (!formData.product_id.trim()) {
      setError('Product ID is required');
      return;
    }
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    try {
      const result = await createProduct({
        product_id: formData.product_id.trim().toLowerCase(),
        name: formData.name.trim(),
        default_callback_url: formData.default_callback_url.trim() || undefined,
      });
      setDialogOpen(false);
      setRevealed(result);
      setCopied(false);
      loadProducts();
    } catch (err: any) {
      setError(err.message || 'Failed to create product');
    }
  };

  const handleRotate = async (product: Product) => {
    if (!window.confirm(
      `Rotate the shared secret for "${product.name}"?\n\n` +
      `The current secret will stop working immediately. Any caller using it ` +
      `must be updated with the new secret.`
    )) {
      return;
    }
    try {
      const result = await rotateProductSecret(product.product_id);
      setRevealed(result);
      setCopied(false);
      loadProducts();
    } catch (err: any) {
      setError(err.message || 'Failed to rotate secret');
    }
  };

  const handleToggleActive = async (product: Product) => {
    try {
      await updateProduct(product.product_id, { active: !product.active });
      loadProducts();
    } catch (err: any) {
      setError(err.message || 'Failed to update product');
    }
  };

  const handleDelete = async (product: Product) => {
    if (!window.confirm(
      `Delete product "${product.name}" (${product.product_id})?\n\n` +
      `This permanently deletes its shared secret. Callers using this product ` +
      `will be rejected with 401.`
    )) {
      return;
    }
    try {
      await deleteProduct(product.product_id);
      loadProducts();
    } catch (err: any) {
      setError(err.message || 'Failed to delete product');
    }
  };

  const handleCopySecret = async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed.secret);
      setCopied(true);
    } catch {
      // Clipboard API may be unavailable (e.g. non-HTTPS); user can select manually.
      setCopied(false);
    }
  };

  const formatDate = (isoString?: string) =>
    isoString ? new Date(isoString).toLocaleString() : '-';

  const sortedProducts = [...products].sort((a, b) =>
    a.product_id.localeCompare(b.product_id)
  );

  return (
    <>
      <Header />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Products
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Add Product
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Products own the per-product JWT shared secrets that authenticate callers of the
          scoring API. A request to <code>/api/v1/submit-responses</code> must include a{' '}
          <code>product</code> field; the service verifies the request's{' '}
          <code>Authorization: Bearer</code> token with that product's secret.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Shared secrets are generated server-side and stored in AWS Secrets Manager. The
          secret value is shown <strong>only once</strong>, at creation or rotation — it cannot
          be retrieved later, only rotated.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Default Callback URL</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedProducts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No products defined
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedProducts.map((p) => (
                      <TableRow key={p.product_id}>
                        <TableCell>
                          <code style={{
                            backgroundColor: '#f5f5f5',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontFamily: 'monospace',
                          }}>
                            {p.product_id}
                          </code>
                        </TableCell>
                        <TableCell>{p.name}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={p.active ? 'Active' : 'Disabled'}
                            color={p.active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {p.default_callback_url ? (
                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                              {p.default_callback_url}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{formatDate(p.created_at)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Rotate secret">
                              <IconButton size="small" onClick={() => handleRotate(p)}>
                                <RotateIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={p.active ? 'Disable product' : 'Enable product'}>
                              <IconButton size="small" onClick={() => handleToggleActive(p)}>
                                {p.active
                                  ? <DisableIcon fontSize="small" />
                                  : <EnableIcon fontSize="small" color="success" />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete product">
                              <IconButton size="small" onClick={() => handleDelete(p)} color="error">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Add product dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Product</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Product ID"
                value={formData.product_id}
                onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}
                helperText="Lowercase letters, digits, '-' or '_' (e.g. star, staff-portal). Used in the request's product field."
                fullWidth
                required
              />
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                helperText="Human-readable product name"
                fullWidth
                required
              />
              <TextField
                label="Default Callback URL (Optional)"
                value={formData.default_callback_url}
                onChange={(e) => setFormData({ ...formData, default_callback_url: e.target.value })}
                helperText="Reference only — each request still supplies its own callback_url"
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} variant="contained" color="primary">
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* One-time secret reveal dialog */}
        <Dialog open={!!revealed} onClose={() => setRevealed(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Shared secret for {revealed?.product?.name}</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Copy this secret now — it will <strong>not be shown again</strong>. Store it
              securely; the caller signs its JWT with it.
            </Alert>
            <DialogContentText sx={{ mb: 1 }}>
              Product ID: <code>{revealed?.product?.product_id}</code>
            </DialogContentText>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                value={revealed?.secret || ''}
                fullWidth
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
                multiline
              />
              <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                <IconButton onClick={handleCopySecret} color={copied ? 'success' : 'default'}>
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRevealed(null)} variant="contained">
              Done
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default ProductManagement;
